import { Button, DatePicker, Input, Modal, Space, Table } from "antd";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getReferralPartnersRequest,
  removeReferralPartnerRequest,
} from "../../feature/referral-partners/referralPartnerSlice";
import { appConfig } from "../../utils/appConfig";
import { referralPartnerSelector } from "../../feature/referral-partners/referralPartnerSelector";
import { parseToMoney } from "../../utils/helper";
import DebounceSelect from "../../components/DebounceSelect";
import { searchPartner } from "../../feature/referral-partners/referralPartnerApi";

const ReferralPartners = () => {
  const [page, setPage] = useState(1);
  const [startDate, setStartDate] = useState(moment("2022-01-01"));
  const [endDate, setEndDate] = useState(moment(new Date()));
  const [pageSize, setPageSize] = useState(appConfig.PAGE_SIZE);
  const [selectedReferralPartner, setSelectedReferralPartner] = useState();
  const [displayRemoveModal, setDisplayRemoveModal] = useState(false);
  const [searchType, setSearchType] = useState("date");
  const [partnerSearchContent, setPartnerSearchContent] = useState("");
  const {
    listReferralPartner,
    total,
    isGetReferralPartners,
    isRemoveReferralPartner,
  } = useSelector(({ referralPartnersState }) =>
    referralPartnerSelector(referralPartnersState)
  );
  const dispatch = useDispatch();
  const totalRef = useRef(total);

  const columns = useMemo(() => {
    const result = [
      {
        title: "Referral Partner Name",
        dataIndex: "partnerName",
        key: "partnerName",
        render: (_, item) => (
          <div
            className="pointer"
            onClick={() =>
              window.open(`/admin/referral-partner/${item._id}/${item.code}`)
            }
          >{`${item.firstName || ""} ${item.lastName || ""}`}</div>
        ),
      },
      {
        title: "Referral Partner Code",
        dataIndex: "code",
        key: "code",
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
      },
      {
        title: "PayPal Email",
        dataIndex: "paypalEmail",
        key: "paypalEmail",
      },
      {
        title: "Created On",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (time) => moment(time).format("DD MMM YYYY"),
      },
      {
        title: "Commission Earned",
        dataIndex: "commissionEarned",
        key: "commissionEarned",
        render: (value) => (value ? parseToMoney(value) : ""),
      },
      {
        title: "Payment Made",
        dataIndex: "paymentMade",
        key: "paymentMade",
        render: (value) => (value ? parseToMoney(value) : ""),
      },
      {
        title: "W-9",
        dataIndex: "isProvided",
        key: "isProvided",
        render: (value) => (value ? "Yes" : "No"),
      },
      {
        title: "Action",
        key: "action",
        render: (_, item) => (
          <Space size="middle">
            <span
              className="link-pointer"
              onClick={() =>
                window.open(`/admin/referral-partner/${item._id}/${item.code}`)
              }
            >
              View
            </span>
            <span
              className="pointer ant-typography ant-typography-danger"
              onClick={() => onRemoveReferralPartner(item)}
            >
              Delete
            </span>
          </Space>
        ),
      },
    ];
    return result;
    // eslint-disable-next-line
  }, []);

  const onRemoveReferralPartner = (data) => {
    setSelectedReferralPartner(data);
    setDisplayRemoveModal(true);
  };

  const onConfirmRemove = () => {
    selectedReferralPartner &&
      dispatch(removeReferralPartnerRequest(selectedReferralPartner));
    setDisplayRemoveModal(false);
  };

  const loadData = (newPage, sizeChanger, type, value) => {
    const limit = sizeChanger || pageSize;
    const st = moment(startDate).endOf("day").toISOString();
    const et = moment(endDate).endOf("day").toISOString();
    setPage(newPage);
    setPageSize(sizeChanger);
    setSearchType(type);
    setPartnerSearchContent(value);
    if (type === "partner") {
      dispatch(
        getReferralPartnersRequest({
          q: value || "",
          limit,
          offset: (newPage - 1) * limit,
          type: "partner",
        })
      );
    } else if (type === "commissionSort") {
      dispatch(
        getReferralPartnersRequest({
          st,
          et,
          limit,
          offset: (newPage - 1) * limit,
          type: "commissionSort",
        })
      );
    } else {
      dispatch(
        getReferralPartnersRequest({
          st,
          et,
          limit,
          offset: (newPage - 1) * limit,
          type: "date",
        })
      );
    }
  };

  const onFilterDate = () => {
    loadData(1, pageSize, "date");
  };

  const onSearchPartner = (value) => {
    loadData(1, pageSize, "partner", value);
  };

  const onFilterDateSort = () => {
    loadData(1, pageSize, "commissionSort");
  };

  const onChangeTable = (page, sizeChanger) => {
    loadData(page, sizeChanger, searchType, partnerSearchContent);
  };

  useEffect(() => {
    if (
      total < totalRef.current &&
      page !== total / pageSize &&
      page > 1 &&
      total > 0 &&
      total % pageSize === 0
    ) {
      loadData(page - 1, pageSize, searchType, partnerSearchContent);
      totalRef.current = total;
      return;
    }
    if (total > totalRef.current) {
      loadData(1, pageSize, searchType, partnerSearchContent);
      totalRef.current = total;
      return;
    }
    loadData(page, pageSize, searchType, partnerSearchContent);
    totalRef.current = total;
    // eslint-disable-next-line
  }, [total]);

  return (
    <div>
      <div className="title-page">Referral Partners</div>
      <div className="filter-container mt-25 d-flex space-between">
        <div className="d-flex mb-5">
          <div className="mr-5">
            <div>Start Date</div>
            <DatePicker
              placeholder="DD MMM YYYY"
              format="DD MMM YYYY"
              onChange={(date) => setStartDate(date)}
              value={startDate}
            />
          </div>
          <div className="mr-25">
            <div>End Date</div>
            <DatePicker
              placeholder="DD MMM YYYY"
              format="DD MMM YYYY"
              onChange={(date) => setEndDate(date)}
              value={endDate}
            />
          </div>
          <div className="mr-25">
            <div className="hidden">filter ledger</div>
            <Button type="primary" onClick={onFilterDate}>
              Re-calculate Commission & Payment
            </Button>
          </div>
          <div className="mr-25">
            <div className="hidden">filter ledger</div>
            <Button type="primary" onClick={onFilterDateSort}>
              Sort Commission Earned
            </Button>
          </div>
        </div>
        <div className="d-flex f-right">
          <div>
            <div>
              Search partner by name, partner code, email or PayPal email
            </div>
            <Input.Search
              className="w-400"
              placeholder="Type name, code, email or PayPal email"
              onSearch={(value) => onSearchPartner(value)}
              enterButton
              allowClear
            />
          </div>
        </div>
      </div>
      <Table
        className="mt-25"
        columns={columns}
        dataSource={listReferralPartner.length > 0 ? listReferralPartner : []}
        bordered
        pagination={{
          showTotal: (total) => `Total ${total} results`,
          defaultPageSize: appConfig.PAGE_SIZE,
          pageSize,
          defaultCurrent: 1,
          current: page,
          showSizeChanger: true,
          pageSizeOptions: [appConfig.PAGE_SIZE, 25, 50].sort((a, b) => a - b),
          total,
          onChange: (page, sizeChanger) => onChangeTable(page, sizeChanger),
        }}
        loading={isGetReferralPartners || isRemoveReferralPartner}
        rowKey="_id"
      />
      {displayRemoveModal && (
        <Modal
          title="Are you sure?"
          visible={true}
          width={"50%"}
          className="modal-w-600"
          onOk={onConfirmRemove}
          onCancel={() => setDisplayRemoveModal(false)}
        >
          <p>
            Do you really want to delete this partner? This process cannot be
            undone.
          </p>
        </Modal>
      )}
    </div>
  );
};

export default ReferralPartners;
